
import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { useLocation } from 'react-router-dom';
import { useModalStore } from "@/services/modalStore";
import useErrorLogger from '@/hooks/useErrorLogger';

interface LogEntry {
  message: string;
  timestamp: number;
  type: 'info' | 'warn' | 'error' | 'nav' | 'modal';
}

// Singleton per il logger visivo
export class VisualLogger {
  private static instance: VisualLogger;
  private logs: LogEntry[] = [];
  private listeners: Array<(logs: LogEntry[]) => void> = [];
  private static MAX_LOGS = 200;
  private static enabled = true;
  private pendingLogs: LogEntry[] = [];
  private flushTimeout: NodeJS.Timeout | null = null;

  // env flags
  private static isProd =
    ((import.meta as any).env?.MODE === 'production') ||
    (typeof process !== 'undefined' && process.env.NODE_ENV === 'production');
  private static debugEnabled =
    ((import.meta as any).env?.VITE_VISUAL_DEBUGGER === 'true') ||
    (typeof process !== 'undefined' && process.env.VITE_VISUAL_DEBUGGER === 'true') ||
    (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('debug') === 'keyboard');

  private constructor() {}

  static getInstance(): VisualLogger {
    if (!VisualLogger.instance) {
      VisualLogger.instance = new VisualLogger();
    }
    return VisualLogger.instance;
  }

  static setEnabled(enabled: boolean): void {
    VisualLogger.enabled = enabled;
  }

  static isEnabled(): boolean {
    return VisualLogger.enabled;
  }

  log(message: string, type: LogEntry['type'] = 'info'): void {
    if (!VisualLogger.enabled) return;
    if (
      VisualLogger.isProd &&
      type === 'info' &&
      !VisualLogger.debugEnabled
    ) {
      return;
    }

    const entry = {
      message,
      timestamp: Date.now(),
      type,
    };

    this.pendingLogs.push(entry);
    if (!this.flushTimeout) {
      this.flushTimeout = setTimeout(() => {
        const logsToAdd = [...this.pendingLogs];
        this.pendingLogs = [];
        this.flushTimeout = null;

        logsToAdd.reverse().forEach((e) => {
          this.logs.unshift(e);
          if (this.logs.length > VisualLogger.MAX_LOGS) {
            this.logs.pop();
          }
        });
        this.notifyListeners();
      }, 100);
    }
  }

  
  subscribe(listener: (logs: LogEntry[]) => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  private notifyListeners(): void {
    // Using a microtask prevents nested React updates when logs are generated
    // during component unmount phases. This avoids "Maximum update depth"
    // errors triggered by synchronous setState calls in effects cleanup.
    queueMicrotask(() => {
      this.listeners.forEach(listener => listener([...this.logs]));
    });
  }

  clearLogs(): void {
    this.logs = [];
    this.notifyListeners();
  }

  getLogs(): LogEntry[] {
    return [...this.logs];
  }
}

// Funzioni di utility per utilizzare il logger dal codice
export const visualLog = {
  info: (message: string) => VisualLogger.getInstance().log(message, 'info'),
  warn: (message: string) => VisualLogger.getInstance().log(message, 'warn'),
  error: (message: string) => VisualLogger.getInstance().log(message, 'error'),
  nav: (message: string) => VisualLogger.getInstance().log(message, 'nav'),
  modal: (message: string) => VisualLogger.getInstance().log(message, 'modal'),
  clear: () => VisualLogger.getInstance().clearLogs(),
  enable: () => VisualLogger.setEnabled(true),
  disable: () => VisualLogger.setEnabled(false),
  isEnabled: () => VisualLogger.isEnabled(),
};

// Hook per utilizzare il logger
export const useVisualLogger = () => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const logger = VisualLogger.getInstance();

  useEffect(() => {
    const unsubscribe = logger.subscribe(setLogs);
    return unsubscribe;
  }, [logger]);

  const clearLogs = () => logger.clearLogs();

  return { logs, clearLogs };
};

// Componente per visualizzare i log
const VisualDebugger = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { logs, clearLogs } = useVisualLogger();
  const location = useLocation();
  const { activeModal } = useModalStore();
  const logError = useErrorLogger();
  const containerRef = useRef<HTMLDivElement>(null);
  const [headerOffset, setHeaderOffset] = useState(0);

  const [search, setSearch] = useState('');
  const [visibleTypes, setVisibleTypes] = useState<Record<LogEntry['type'], boolean>>({
    info: true,
    warn: true,
    error: true,
    nav: true,
    modal: true,
  });

  // Ensure the main application root remains interactive when the debugger is mounted
  useEffect(() => {
    const root = document.getElementById('root') as (HTMLElement & { inert?: boolean }) | null;
    if (root) {
      root.removeAttribute('aria-hidden');
      (root as any).inert = false;
    }
  }, []);

  useLayoutEffect(() => {
    const header = document.querySelector('header');
    if (header) {
      setHeaderOffset((header as HTMLElement).offsetHeight);
    }
  }, []);

  const filteredLogs = logs.filter(
    l => visibleTypes[l.type] && l.message.toLowerCase().includes(search.toLowerCase())
  );

  const exportLogs = () => {
    const data = JSON.stringify(filteredLogs, null, 2);
    const url = URL.createObjectURL(new Blob([data], { type: 'application/json' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'logs.json';
    a.click();
    URL.revokeObjectURL(url);
  };
  
  // Flag per prevenire azioni ripetute
  const isProcessingActionRef = useRef<boolean>(false);
  // Timeout per resettare il flag
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const toggleExpanded = () => setIsExpanded(!isExpanded);

  const toggleType = (t: LogEntry['type']) =>
    setVisibleTypes(prev => ({ ...prev, [t]: !prev[t] }));

  const copyLogsToClipboard = () => {
    const logText = logs
      .map(log => `${new Date(log.timestamp).toLocaleTimeString()} [${log.type.toUpperCase()}]: ${log.message}`)
      .join('\n');

    navigator.clipboard.writeText(logText)
      .then(() => visualLog.info("Log copiato negli appunti"))
      .catch(err => visualLog.error("Errore durante la copia negli appunti"));
  };

  const sendLogsToServer = async () => {
    const currentLogs = VisualLogger.getInstance().getLogs();
    try {
      await logError('VisualDebugger logs', { additionalInfo: currentLogs });
      visualLog.info('Log inviati a Sentry');
    } catch (err) {
      visualLog.error('Errore invio log');
    }
  };

  useEffect(() => {
    const handleScroll = (e: Event) => {
      lastScrolledRef.current = e.target as HTMLElement;
    };
    document.addEventListener('scroll', handleScroll, true);
    return () => document.removeEventListener('scroll', handleScroll, true);
  }, []);

  const lastScrolledRef = useRef<HTMLElement | null>(null);

  const takeSnapshot = () => {
    const snapshot = {
      timestamp: Date.now(),
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
        scrollX: window.scrollX,
        scrollY: window.scrollY,
      },
      deviceInfo: {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        pixelRatio: window.devicePixelRatio,
      },
      lastScrolled: lastScrolledRef.current
        ? {
            tag: lastScrolledRef.current.tagName,
            id: lastScrolledRef.current.id,
            className: lastScrolledRef.current.className,
            scrollTop: (lastScrolledRef.current as HTMLElement).scrollTop,
          }
        : null,
    };

    const blob = new Blob([JSON.stringify(snapshot, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `snapshot-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
    visualLog.info('Snapshot salvato');
  };
  
  // Funzione wrapper per tutte le azioni per evitare doppi click
  const executeAction = (action: () => void, actionName: string) => {
    if (isProcessingActionRef.current) {
      visualLog.warn(`Azione ${actionName} ignorata - già in corso`);
      return;
    }
    
    isProcessingActionRef.current = true;
    
    // Esegui l'azione
    action();
    
    // Reset del flag dopo un breve timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      isProcessingActionRef.current = false;
    }, 300); // Timeout sufficiente per evitare doppie azioni
  };

  useEffect(() => {
    visualLog.nav(`Path: ${location.pathname}`);
  }, [location.pathname]);

  useEffect(() => {
    if (activeModal) {
      visualLog.modal(`Modal opened: ${activeModal}`);
    }
  }, [activeModal]);
  
  // Pulizia dei timeout al dismount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  if (!VisualLogger.isEnabled()) return null;

  return (
    <div
      ref={containerRef}
      className="sticky left-0 right-0 z-[9999] bg-yellow-400 text-black"
      style={{ top: headerOffset }}
    >
      <div className="flex flex-col">
        <div className="p-2">
          {/* Pulsanti su una singola riga */}
          <div className="flex items-center gap-1 flex-wrap mb-1">
            <Button
              variant="outline"
              size="sm"
              className="h-6 px-2 bg-white"
              onClick={toggleExpanded}
            >
              {isExpanded ? 'Hide' : 'Show'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-6 px-2 bg-white"
              onClick={() => executeAction(clearLogs, "Clear logs")}
            >
              Clear
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-6 px-2 bg-white"
              onClick={() => executeAction(copyLogsToClipboard, "Copy logs")}
            >
              Copy
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-6 px-2 bg-white"
              onClick={() => executeAction(sendLogsToServer, "Send logs")}
            >
              Send
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-6 px-2 bg-white"
              onClick={() => executeAction(takeSnapshot, "Snapshot")}
            >
              Snapshot
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-6 px-2 bg-white"
              onClick={() => executeAction(() => visualLog.disable(), "Disabilita")}
            >
              <X size={14} />
            </Button>
          </div>
          <div>
            <span className="font-bold">Debug</span>
            <span className="ml-3 text-sm">
              Path: {location.pathname} {activeModal ? `| Modal: ${activeModal}` : ''}
            </span>
          </div>
        </div>
        {isExpanded && (
          <div className="space-y-2 p-2 bg-white text-xs z-[10000]" style={{ position: 'relative' }}>
            <div className="flex flex-wrap items-center gap-2">
              {(['info','warn','error','nav','modal'] as LogEntry['type'][]).map(t => (
                <label key={t} className="flex items-center gap-1 text-[10px] md:text-xs">
                  <input
                    aria-label={t}
                    type="checkbox"
                    checked={visibleTypes[t]}
                    onChange={() => toggleType(t)}
                  />
                  {t}
                </label>
              ))}
              <input
                aria-label="Cerca log"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Cerca"
                className="border p-1 text-[10px] md:text-xs rounded"
              />
              <Button variant="outline" size="sm" className="h-6 px-2" onClick={() => executeAction(exportLogs,'Export logs')}>
                Export
              </Button>
            </div>

            <div className="max-h-40 overflow-y-auto">
            {filteredLogs.map((log, index) => (
              <div
                key={index}
                className={`mb-1 p-1 rounded ${
                  log.type === 'error' ? 'bg-red-100' :
                  log.type === 'warn' ? 'bg-yellow-100' :
                  log.type === 'modal' ? 'bg-purple-100' :
                  log.type === 'nav' ? 'bg-blue-100' : 'bg-gray-100'
                }`}
              >
                <span className="font-mono">
                  {new Date(log.timestamp).toLocaleTimeString()}: {log.message}
                </span>
              </div>
            ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VisualDebugger;
